import { useSearchStore } from "@app/stores/useSearchStore"
import { useHead, useRequestURL, useRouter, useSeoMeta } from "nuxt/app";
import { watch } from "vue";
import IExtendedSearchQueryFilter from "@app/interfaces/dtos/IExtendedSearchQueryFilter";
import type { IMetaInfo } from "@domain/interfaces/dtos/IMetaInfo";
import { UseMetaVariablesWatcherOptions } from '@app/interfaces/useMetaVariablesWatcher.interface';
import { FilterProcessingService } from "@app/routing/FilterProcessingService";
import { usePortalMap } from "./usePortalMap";
import { getPortalTypeFromEnvClient } from "@app/utils/get-portal-type-from-env.client";
import { getPortalTypeFromEnvServer } from "@app/utils/get-portal-type-from-env.server";

export const useGetDomain = (): string | undefined => {
	const portalMap = usePortalMap();
	const portalType = import.meta.server ? getPortalTypeFromEnvServer() : getPortalTypeFromEnvClient();
	const origin = portalMap.get(portalType);

	return origin || useRequestURL().origin;
}

const useCanonicalPath = (filterSelection: IExtendedSearchQueryFilter[]):string =>{
	const router = useRouter();
	const origin = useGetDomain();
	const requestUrl = useRequestURL();
	const searchStore = useSearchStore();
	const seoMeta = searchStore.pageData?.seoMeta;
	const robots = seoMeta?.robots?.toLowerCase();
	const isIndexable = robots ? !robots.includes('noindex') : false;
	const currentPath = `${origin}${requestUrl.pathname}`;

	if (isIndexable) {
		return currentPath;
	}

	try {
		const indexableFilters = seoMeta?.hasEnoughResults
			? filterSelection.filter(f => f.isIndexable)
			: [];

		const route = FilterProcessingService.getInstance().processFilters(router, indexableFilters);
		const path = router.resolve(route).path;

		return `${origin}${path}`;
	} catch {
		return currentPath;
	}
}

const useSetHeadSeoMeta = (
	options: UseMetaVariablesWatcherOptions
):void => {
	const canonicalPath = useCanonicalPath(options.filterSelection);

	if (import.meta.server) {
		const image = "https://monet-prtl-co.imgix.net/Shared/SearchPage/BA_Portal_no%20text_illustration.png?auto=compress&h=400&w=auto";
		useSeoMeta({
			charset: "utf-8",
			viewport: "width=device-width, initial-scale=1",
			ogType: "website",
			ogImage: image,
		});
	}

	useSeoMeta({
		title: () => options.metaInfoExtended.metaTitle,
		description: () => options.metaInfoExtended.metaDescription,
		robots: () => options.metaInfoExtended.robots,
		ogTitle: () => options.metaInfoExtended.metaTitle,
		ogDescription: () => options.metaInfoExtended.metaDescription,
	});

	useHead(() => ({
		link: [
			{ rel: "canonical", href: canonicalPath },
		],
	}));
};

export const useMetaVariablesWatcher = ():void => {
	const searchStore = useSearchStore();

	const seoMeta = searchStore.pageData?.seoMeta;

	const pageTitle = seoMeta?.pageTitle || "";
	const metaTitle = seoMeta?.metaTitle || "";
	const metaDescription = seoMeta?.metaDescription || "";
	const robots = seoMeta?.robots || "noindex, noarchive";
	const pageTitleWithoutResultsCount = seoMeta?.pageTitleWithoutResultsCount || "";
	const hasEnoughResults = seoMeta?.hasEnoughResults || false;
	const metaInfoExtended: IMetaInfo = { pageTitle, metaTitle, metaDescription, robots, pageTitleWithoutResultsCount, hasEnoughResults };

	useHead(() => {
		if (!searchStore.pageData) return {};

		const { schema } = searchStore.pageData;

		return {
			script: [{
				type: "application/ld+json",
				innerHTML: JSON.stringify(schema),
			}]
		};
	});

	useSetHeadSeoMeta({
		filterSelection: searchStore.filtersSelection,
		metaInfoExtended,
	});

	searchStore.setPageTitle(pageTitle);

	watch(() => searchStore.pageData?.seoMeta, (newSeoMeta) => {
		if(!newSeoMeta) return;

		searchStore.setPageTitle(newSeoMeta.pageTitle);

		useSetHeadSeoMeta({
			filterSelection: searchStore.filtersSelection,
			metaInfoExtended: {
				pageTitle: newSeoMeta.pageTitle,
				metaTitle: newSeoMeta.metaTitle,
				metaDescription: newSeoMeta.metaDescription,
				robots: newSeoMeta.robots,
				pageTitleWithoutResultsCount: newSeoMeta.pageTitleWithoutResultsCount,
				hasEnoughResults: newSeoMeta.hasEnoughResults,
			},
		});
	}, { immediate: true });
}
